import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay-ts';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { useIdleTimer } from 'react-idle-timer';
import SupportLang from '../../../Common/SupportLang';
import { addAnswer, addDirectorMessage, addImage, addNote, addWriteIn, clearAnswerImage, removeAnswer } from '../../../redux/survey/actions';
import { postData } from '../../helpers/fetch_services';
import { submitClientConnectAnswers } from './helpers/clientConnectSubmit';
import { isClientConnectExcludedUser } from '../../helpers/clientConnectExcluded';
import {
  getClientConnectQuestionValidationError,
  getClientConnectVisibleQuestions,
} from './helpers/clientConnectSurveyQuestions';
import { createSurveyAnswerHandler, getStoredAnswer, resolveScoreExclude } from '../../helpers/createSurveyAnswerHandler';
import {
  filterQuestionsThroughFinish,
  getQuestionIdsAfter,
  omitAnswersByQuestionIds,
} from '../../helpers/surveyFinishSurvey';
import { buildBrandThemeStyle, resolveBrandPrimaryColor } from './helpers/brandThemeStyle';
import ClientConnectV2Content from './components/ClientConnectV2Content';
import { normalizeId } from './survey/surveyPreviewUtils';
import './client-connect-v2.css';
import './client-connect-v2-page.css';

function ClientConnectV2({
  survey,
  theme,
  addAnswer_,
  addDirectorMessage_,
  addWriteIn_,
  addNote_,
  addImage_,
  clearAnswerImage_,
  removeAnswer_,
}) {
  const intl = useIntl();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const submitSucceededRef = useRef(submitSucceeded);
  const idleSubmitEnabledRef = useRef(false);
  const finishSurveyInFlightRef = useRef(false);
  const [surveyErrors, setSurveyErrors] = useState({});

  const currentSurvey = survey?.survey_without_login;
  const clientConnect = currentSurvey?.clientConnect;
  const isAuditComplete = clientConnect?.userAuditComplete === true;

  const brandPrimaryColor = useMemo(() => resolveBrandPrimaryColor(theme), [theme]);
  const brandThemeStyle = useMemo(() => buildBrandThemeStyle(brandPrimaryColor), [brandPrimaryColor]);

  const brandDetails = useMemo(
    () => ({
      logo: localStorage.getItem('logo') || theme?.logo,
      primaryColor: brandPrimaryColor,
      secondaryColor: theme?.secondaryColor,
    }),
    [theme, brandPrimaryColor]
  );

  const externalAnswers = useMemo(() => ({ ...(survey?.answeres || {}) }), [survey?.answeres]);

  const questionById = useMemo(() => {
    const map = new Map();
    (currentSurvey?.questions || []).forEach((question) => {
      map.set(normalizeId(question._id), question);
    });
    return map;
  }, [currentSurvey]);

  const handleSetAnswer = useCallback(
    createSurveyAnswerHandler({
      answers: survey?.answeres,
      questionById,
      addAnswer: addAnswer_,
      addWriteIn: addWriteIn_,
      addNote: addNote_,
      addImage: addImage_,
      clearAnswerImage: clearAnswerImage_,
      onValidationChange: setSurveyErrors,
      getValidationError: (question, projectedAnswer) => (
        getClientConnectQuestionValidationError(question, projectedAnswer, intl)
      ),
    }),
    [addAnswer_, addImage_, addNote_, addWriteIn_, clearAnswerImage_, intl, questionById, survey?.answeres],
  );

  const handleSetQuestionError = useCallback((questionId, error) => {
    const key = normalizeId(questionId);
    setSurveyErrors((prev) => {
      if (!error) {
        if (!prev[key] && !prev[questionId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        delete next[questionId];
        return next;
      }
      return { ...prev, [key]: error };
    });
  }, []);

  const updateRecipientStatus = async () => {
    if (localStorage.getItem('client_connect_id') && localStorage.getItem('email')) {
      await postData('survey_sent/client_connect/updateRecipientsStatus', {
        client_connect_id: localStorage.getItem('client_connect_id'),
        email: localStorage.getItem('email'),
      });
    }
  };

  useEffect(() => {
    updateRecipientStatus();
  }, []);

  const isExcludedUser = useMemo(
    () => isClientConnectExcludedUser(clientConnect),
    [clientConnect]
  );

  const validateSurvey = useCallback(() => {
    const answers = survey?.answeres || {};
    const questions = getClientConnectVisibleQuestions(currentSurvey, answers);

    let hasErrors = false;
    const nextErrors = {};

    questions.forEach((question) => {
      const answerKey = normalizeId(question._id);
      const answer = getStoredAnswer(answers, question._id);
      const error = getClientConnectQuestionValidationError(question, answer, intl);
      if (error) {
        nextErrors[answerKey] = error;
        hasErrors = true;
      }
    });

    setSurveyErrors(nextErrors);
    return hasErrors;
  }, [currentSurvey, intl, survey?.answeres]);

  const redirectToThankYou = useCallback(() => {
    submitSucceededRef.current = true;
    setSubmitSucceeded(true);
    history.push({
      pathname: '/surveyThankYou',
      state: { surveyWithoutLogin: true },
    });
  }, [history]);

  const submitAnswers = async (answersOverride = null) => {
    if (isAuditComplete || isExcludedUser) {
      return false;
    }
    setLoading(true);
    try {
      const success = await submitClientConnectAnswers(
        answersOverride
          ? { ...survey, answeres: answersOverride }
          : survey,
      );
      if (success) {
        localStorage.removeItem('location');
        redirectToThankYou();
        return true;
      }
      toast.error(intl.formatMessage({ id: 'error-saving-your-response' }));
    } catch (e) {
      toast.error(intl.formatMessage({ id: 'error-saving-your-response' }));
    } finally {
      if (!submitSucceededRef.current) {
        setLoading(false);
      }
    }
    return false;
  };

  const handleFinalSubmit = async () => {
    if (submitSucceededRef.current) {
      return true;
    }
    return submitAnswers();
  };

  /** Option-level finish_survey: clear later answers and submit (Normal / CC v1). */
  const handleFinishSurvey = useCallback(
    async (question, selectedValue) => {
      if (
        finishSurveyInFlightRef.current ||
        loading ||
        submitSucceededRef.current ||
        isAuditComplete ||
        isExcludedUser
      ) {
        return;
      }
      finishSurveyInFlightRef.current = true;

      const answerKey = normalizeId(question._id);
      const projectedAnswer = {
        ...getStoredAnswer(survey?.answeres, question._id),
        value: selectedValue,
        score_exclude: resolveScoreExclude(question, selectedValue),
      };
      const error = getClientConnectQuestionValidationError(
        question,
        projectedAnswer,
        intl,
      );
      if (error) {
        finishSurveyInFlightRef.current = false;
        setSurveyErrors((prev) => ({ ...prev, [answerKey]: error }));
        return;
      }

      const laterIds = getQuestionIdsAfter(currentSurvey?.questions, question._id);
      // Keep later answers when computing hide/route visibility so questions they
      // hide do not reappear. Strip them only from the submit payload.
      const answersWithFinish = {
        ...(survey?.answeres || {}),
        [answerKey]: projectedAnswer,
      };
      const answersForSubmit = omitAnswersByQuestionIds(
        answersWithFinish,
        laterIds,
      );

      const visibleThroughFinish = filterQuestionsThroughFinish(
        currentSurvey?.questions,
        getClientConnectVisibleQuestions(currentSurvey, answersWithFinish),
        question._id,
      );

      const nextErrors = {};
      visibleThroughFinish.forEach((visibleQuestion) => {
        const answer =
          normalizeId(visibleQuestion._id) === answerKey
            ? projectedAnswer
            : getStoredAnswer(answersForSubmit, visibleQuestion._id);
        const questionError = getClientConnectQuestionValidationError(
          visibleQuestion,
          answer,
          intl,
        );
        if (questionError) {
          nextErrors[normalizeId(visibleQuestion._id)] = questionError;
        }
      });

      if (Object.keys(nextErrors).length) {
        finishSurveyInFlightRef.current = false;
        setSurveyErrors(nextErrors);
        return;
      }

      try {
        const success = await submitAnswers(answersForSubmit);
        if (success) {
          laterIds.forEach((id) => {
            removeAnswer_({ id: normalizeId(id) });
          });
        }
      } catch {
        // submitAnswers already toasts; keep in-flight clear in finally
      } finally {
        // Always clear so a failed finish can be retried; success uses
        // submitSucceededRef / unmount to block doubles.
        finishSurveyInFlightRef.current = false;
      }
    },
    [
      currentSurvey,
      intl,
      isAuditComplete,
      isExcludedUser,
      loading,
      removeAnswer_,
      survey,
    ],
  );

  const handleIdleSubmitEnabledChange = useCallback((enabled) => {
    idleSubmitEnabledRef.current = enabled;
  }, []);

  // Auto-submit when idle on the final step (same as Client Connect v1). Does not check
  // leadershipReplySaved; that flag only gates the manual Save & Finish button in step 3.
  useIdleTimer({
    timeout: 1000 * (Number(currentSurvey?.ideal_duration) || 60),
    onIdle: () => {
      if (
        !submitSucceededRef.current &&
        !isAuditComplete &&
        !isExcludedUser &&
        idleSubmitEnabledRef.current
      ) {
        handleFinalSubmit();
      }
    },
    debounce: 500,
  });

  if (submitSucceeded) {
    return null;
  }

  if (!currentSurvey || !clientConnect) {
    return (
      <LoadingOverlay active spinner>
        <SupportLang />
        <div className="client-connect-v2-page" style={{ minHeight: '100vh' }} />
      </LoadingOverlay>
    );
  }

  return (
    <LoadingOverlay active={loading} spinner>
      <SupportLang />
      <div
        className="client-connect-v2-page"
        style={{
          ...(brandThemeStyle || {}),
          backgroundColor: theme?.secondaryColor || 'var(--pal-grey-white-200)',
        }}
      >
        <div className="client-connect-v2-page__inner">
          <ClientConnectV2Content
            survey={currentSurvey}
            brandDetails={brandDetails}
            clientConnect={clientConnect}
            externalAnswers={externalAnswers}
            onSetAnswer={handleSetAnswer}
            onSetQuestionError={handleSetQuestionError}
            onFinishSurvey={handleFinishSurvey}
            onDirectorMessageChange={addDirectorMessage_}
            onSubmit={handleFinalSubmit}
            onExcludedComplete={redirectToThankYou}
            onIdleSubmitEnabledChange={handleIdleSubmitEnabledChange}
            validateSurvey={validateSurvey}
            surveyErrors={surveyErrors}
            isSubmitting={loading}
            isExcludedUser={isExcludedUser}
          />
        </div>
      </div>
    </LoadingOverlay>
  );
}

const mapStateToProps = (state) => ({
  survey: state.survey,
  theme: state.survey.survey_theme,
});

const mapDispatchToProps = {
  addAnswer_: addAnswer,
  addDirectorMessage_: addDirectorMessage,
  addWriteIn_: addWriteIn,
  addNote_: addNote,
  addImage_: addImage,
  clearAnswerImage_: clearAnswerImage,
  removeAnswer_: removeAnswer,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientConnectV2);
